/**
 * Display formatting.
 *
 * Kept as pure functions so the rules that decide how a number reads - and in
 * particular how a missing number reads - are testable without rendering anything.
 * "Not measured" and "zero" must never look alike on screen.
 */

export const NOT_MEASURED = "Not measured";

export function money(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function moneyCompact(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (magnitude >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return money(value);
}

/** A metric value with its unit, or an explicit absence. Never a bare dash. */
export function metricValue(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined) return NOT_MEASURED;
  return `${value}${unit}`;
}

/** A signed gap, so the direction of the vendor's error is visible at a glance. */
export function signedGap(gap: number | null | undefined): string {
  if (gap === null || gap === undefined) return NOT_MEASURED;
  const rounded = Number(gap.toFixed(2));
  return `${rounded > 0 ? "+" : ""}${rounded}`;
}

export function percent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** "2026-06" renders as "Jun 2026". Period strings are never shown raw. */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function periodLabel(period: string): string {
  const [year, month] = period.split("-");
  const index = Number(month) - 1;
  if (!year || Number.isNaN(index) || index < 0 || index > 11) return period;
  return `${MONTHS[index]} ${year}`;
}

export function shortPeriodLabel(period: string): string {
  const [, month] = period.split("-");
  const index = Number(month) - 1;
  return index >= 0 && index <= 11 ? MONTHS[index] : period;
}

export type Severity = "none" | "low" | "med" | "high";

export function severityColor(severity: Severity): string {
  switch (severity) {
    case "high":
      return "var(--rust)";
    case "med":
      return "var(--gold)";
    case "low":
      return "var(--green)";
    default:
      return "var(--slate)";
  }
}

export const PROVENANCE_LABEL: Record<string, string> = {
  client_measured: "Client-measured",
  client_system_shared: "Client system",
  vendor_attested: "Vendor-attested",
  vendor_raw: "Vendor records",
};

/**
 * Whether a metric may back a verified claim. Vendor-attested figures may be shown but
 * never presented as verification.
 */
export function isVerified(provenance: string): boolean {
  return provenance === "client_measured" || provenance === "client_system_shared";
}

export function creditDirectionLabel(direction: string): string {
  switch (direction) {
    case "understated":
      return "Understated by the vendor";
    case "overstated":
      return "Vendor conceded more than computed";
    default:
      return "Vendor figure matches computation";
  }
}

/** "1 record", "105 records". Singular counts appear on low-volume metrics. */
export function recordCount(count: number): string {
  return `${count.toLocaleString()} record${count === 1 ? "" : "s"}`;
}
